/**
 * The state pill and the one primary action for the person in THIS list.
 *
 * The panel does not own any write. Each action is handed in by the host page
 * (`PersonListActions`), which already owns the flows they reuse — the
 * per-contact compare modal and `POST …/contacts/{id}/add-to-search` — and the
 * query invalidations that make the table row and the panel agree afterwards.
 * That keeps `person-panel` free of `features/imports/` internals.
 */
import { type Mutation, useIsMutating } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useState } from "react";

import { Button } from "@nu-assist/ui";

import { thriveContactUrl } from "../../lib/thriveLinks";
import { ACTIONS_SCOPE_HINT } from "./actionContext";
import { type PanelState, type ResolvedState, STATE_PILL_CLASS, stateLabel } from "./personState";

/**
 * The mutation key the host runs the per-contact add under, with
 * `{ contactId }` as its variables. The panel reads "is an add in flight for
 * THIS contact" from the mutation cache rather than from its own state: the
 * view remounts on every ‹ › step, and a local `busy` came back `false` on a
 * walk away and back, re-arming a second add of the same contact
 * (/pr-review #954 iter 1).
 */
export const PERSON_ADD_MUTATION_KEY = ["person-panel", "add-to-search"] as const;

export interface PersonAddVariables {
  contactId: string;
}

/** Filter for "an add of `contactId` is in flight", for `useIsMutating` and
 *  `queryClient.isMutating`. */
export function personAddInFlight(contactId: string | null) {
  return {
    mutationKey: PERSON_ADD_MUTATION_KEY,
    predicate: (m: Mutation<unknown, Error, unknown, unknown>) =>
      contactId !== null &&
      (m.state.variables as PersonAddVariables | undefined)?.contactId === contactId,
  };
}

export interface PersonListActions {
  /** Open the compare flow for this one contact. Also the way to review a
   *  conflict: the compare modal is where the conflict card lives. */
  onCompare: (importContactId: string) => void;
  /** Add this one contact to the list's search; resolves once the outcome is
   *  known (the host reports it). */
  onAddToSearch: (importContactId: string) => Promise<void>;
  /** The action's name on this list ("Add to search"). */
  addLabel: string;
  /** Why the add cannot run here (no linked search, a Company Map list), or null. */
  addBlockedReason: string | null;
  /** The Thrive search the add writes to — named on the button ("Add to search
   *  4512") so the target is never implicit. Null when there is none. */
  addTargetSearchId?: string | null;
  /** What Compare and Add act on, from the PAGE the panel is open over — the
   *  same source as `addTargetSearchId`, so the "Acting on" strip and the
   *  button cannot name different searches (on a derived list the person's
   *  appearance is the ROOT list, not this one; /pr-review #954 iter 3). */
  actingOn?: ActingOn;
}

export interface ActingOn {
  /** This list's title. */
  listName: string;
  /** "Search 4414 · VP Engineering", or null when the add has no search. */
  searchLabel: string | null;
  /** Why there is no search to act on (no search linked, a Company Map list). */
  note: string | null;
}

export function StatePill({ state, thriveId }: { state: PanelState; thriveId: string | null }) {
  return (
    <span
      data-testid="person-state-pill"
      data-state={state}
      className={`inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-label ${STATE_PILL_CLASS[state]}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
      {stateLabel(state, thriveId)}
    </span>
  );
}

export function PersonActions({
  resolved,
  actions,
}: {
  resolved: ResolvedState;
  /** Absent outside a list: the standalone page shows the state only. */
  actions?: PersonListActions;
}) {
  const [error, setError] = useState<string | null>(null);
  const { state, importContactId, thriveContactId } = resolved;
  const inList = Boolean(actions && importContactId);
  const busy = useIsMutating(personAddInFlight(importContactId)) > 0;
  const addName = actions?.addTargetSearchId
    ? `${actions.addLabel} ${actions.addTargetSearchId}`
    : (actions?.addLabel ?? "");

  async function add() {
    if (!actions || !importContactId || busy) return;
    setError(null);
    try {
      await actions.onAddToSearch(importContactId);
    } catch (e) {
      setError(e instanceof Error ? e.message : "The add did not go through.");
    }
  }

  let primary: React.ReactNode = null;
  if (inList && actions && importContactId) {
    if (state === "never_compared") {
      primary = (
        <Button
          data-testid="person-action-compare"
          onClick={() => actions.onCompare(importContactId)}
        >
          Compare to Thrive
        </Button>
      );
    } else if (state === "new" || state === "failed") {
      primary = (
        <Button
          data-testid="person-action-add"
          onClick={() => void add()}
          disabled={busy || actions.addBlockedReason !== null}
          title={actions.addBlockedReason ?? undefined}
        >
          {busy
            ? "Adding…"
            : state === "failed"
              ? `Retry ${addName.charAt(0).toLowerCase()}${addName.slice(1)}`
              : addName}
        </Button>
      );
    } else if (state === "conflict") {
      primary = (
        <Button
          variant="secondary"
          data-testid="person-action-review-conflict"
          onClick={() => actions.onCompare(importContactId)}
        >
          Review conflict
        </Button>
      );
    }
  }
  const showThriveLink = inList && thriveContactId && (state === "in_thrive" || state === "added");

  return (
    <div className="mt-3" data-testid="person-actions">
      <div className="flex flex-wrap items-center gap-2">
        <StatePill state={state} thriveId={thriveContactId} />
        {primary}
        {showThriveLink && thriveContactId && (
          <a
            href={thriveContactUrl(thriveContactId)}
            target="_blank"
            rel="noreferrer"
            data-testid="person-action-open-thrive"
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3.5 py-2 text-label font-semibold text-text hover:border-accent hover:text-accent-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            Open in Thrive
          </a>
        )}
      </div>
      {inList && actions?.addBlockedReason && (state === "new" || state === "failed") && (
        <p className="mt-1 text-caption text-text-muted" data-testid="person-action-add-blocked">
          {actions.addBlockedReason}
        </p>
      )}
      {inList && (
        <p className="mt-1 text-caption text-text-muted" data-testid="person-actions-scope-hint">
          {ACTIONS_SCOPE_HINT}
        </p>
      )}
      {error && (
        <p className="mt-1 text-caption text-error-ink" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
